import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import { CalendarMonth, Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { ConnectedBookingData } from "../../Booking-Form/typings/connected-bookingData";
import {
  colleagueSearchLabel,
  formatColleagueOccupancyTimeRange
} from "./colleague-workplace-floor.functions";

/** Nested table of bookings on one occupied or partial colleague-floor workplace. */
type P = {
  open: boolean;
  inventoryName?: string;
  bookings: ConnectedBookingData[];
  timeFormat: string;
  isOpeningBooking: boolean;
  onClose: () => void;
  onOpenBooking: (booking: ConnectedBookingData) => void;
};

export function ColleagueWorkplaceFloorOccupancyTable({
  open,
  inventoryName,
  bookings,
  timeFormat,
  isOpeningBooking,
  onClose,
  onOpenBooking
}: Readonly<P>) {
  const { t } = useTranslation();
  const title = inventoryName
    ? t("_colleagueFloor_deskBookingsTitle", { name: inventoryName })
    : t("_colleagueFloor_deskBookingsTitleFallback");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid="colleague-workplace-floor-occupancy-table"
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
        <Typography
          component="span"
          variant="h6"
          title={title}
          sx={{
            flex: 1,
            fontFamily: "Fira Sans",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            pr: 1
          }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label={t("Close")}
          onClick={onClose}
          data-testid="colleague-workplace-floor-occupancy-close"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {bookings.length === 0 ? (
          <Typography data-testid="colleague-workplace-floor-occupancy-empty">
            {t("_colleagueFloor_noDeskBookings")}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("Name")}</TableCell>
                <TableCell>{t("_colleagueFloor_from")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.map(booking => {
                const timeLabel = formatColleagueOccupancyTimeRange(booking, timeFormat);
                const rowKey = `${booking.userId}-${booking.bookingId ?? `${booking.start ?? ""}-${booking.end ?? ""}`}`;
                return (
                  <TableRow key={rowKey} data-testid={`colleague-floor-occupancy-row-${rowKey}`}>
                    <TableCell>{colleagueSearchLabel(booking)}</TableCell>
                    <TableCell>
                      {timeLabel === "" ? t("_colleagueFloor_bookingTimeUnknown") : timeLabel}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={<Typography>{t("_colleagueFloor_openBookingTooltip")}</Typography>}
                      >
                        <span>
                          <IconButton
                            disabled={isOpeningBooking}
                            aria-label={t("_colleagueFloor_openBookingTooltip")}
                            data-testid={`colleague-floor-occupancy-open-${rowKey}`}
                            onClick={() => onOpenBooking(booking)}
                          >
                            <CalendarMonth />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
